'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Scrollbar, Autoplay } from 'swiper/modules';
import { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '@/shadcn/lib/utils';
import { BREAKPOINTS } from '@/shared/constants';
import { Img } from '@/shared/components/ui/Img';
import { StarFilledIcon } from '@/shared/components/icons';
import { Separator } from '@/shadcn/components/ui/separator';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface Broker {
    photoUrl: string;
    name: string;
    agencyName?: string;
    location: string;
    saleCount: string;
    rentCount: string;
    isMemberOfNarks: boolean;
    isMemberOfRealunia: boolean;
    isMemberOfZrks: boolean;
    numberOfBrokers?: number;
}

interface Props {
    className?: string | string[];
    type: 'broker' | 'agency';
    withDelay?: number;
    brokers?: Array<Broker>;
    //  agencies?: Array<{}>;
}

export function RealtorsSlider(props: Props) {
    const [sliderInit, setSliderInit] = useState(false);
    const swiperRef: any = useRef(null);
    const paginationRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        let timer: any = 0;
        if (props.withDelay && swiperRef.current?.autoplay) {
            swiperRef.current.autoplay.stop();
            timer = setTimeout(() => {
                swiperRef.current.autoplay.start();
            }, props.withDelay);
        }

        return () => clearTimeout(timer);
    }, [props.withDelay, swiperRef.current]);

    const ITEMS: Array<Broker> = useMemo(() => {
        if (Array.isArray(props.brokers)) return props.brokers;
        return [];
    }, [props.brokers]);

    return (
        <div className={cn('')}>
            <div className={cn('flex justify-center items-center relative')}>
                {/*
                <div
                    className={cn(
                        ACTION_STYLES,
                        '-left-[17px]',
                        'sm:-left-[7px]',
                        'min-[1300px]:-left-[35px]',
                    )}
                    onClick={() => {
                        if (swiperRef?.current) swiperRef.current.slidePrev();
                    }}
                >
                    <div>
                        <ChevronLeftIcon className="size-[25px] -ml-[3px]" />
                    </div>
                </div>
                */}
                <div className={cn('w-full')}>
                    <>
                        {ITEMS.length > 0 && (
                            <Swiper
                                className={cn(`!pb-[80px]`, '!pt-[15px]')}
                                onSwiper={(swiper) => (swiperRef.current = swiper)}
                                modules={[Navigation, Pagination, Scrollbar, Autoplay]}
                                spaceBetween={20}
                                slidesPerView={1}
                                breakpoints={{
                                    [BREAKPOINTS.sm]: {
                                        slidesPerView: 2,
                                        spaceBetween: 0,
                                    },
                                    [BREAKPOINTS.md]: {
                                        slidesPerView: 3,
                                        spaceBetween: 0,
                                    },
                                    [BREAKPOINTS.lg]: {
                                        slidesPerView: 3,
                                        spaceBetween: 0,
                                    },
                                    [BREAKPOINTS.xl]: {
                                        slidesPerView: 4,
                                        spaceBetween: 0,
                                    },
                                    [BREAKPOINTS.xxl]: {
                                        slidesPerView: 5,
                                        spaceBetween: 0,
                                    },
                                }}
                                loop={true}
                                autoplay={{
                                    delay: 3000,
                                    disableOnInteraction: false,
                                }}
                                //  onBeforeInit={(swiper) => {
                                //      if (
                                //          swiper.params.pagination &&
                                //          typeof swiper.params.pagination !== 'boolean'
                                //      ) {
                                //          swiper.params.pagination.el = paginationRef.current;
                                //     }
                                // }}
                                pagination={{
                                    clickable: true,
                                }}
                                onInit={() => setSliderInit(true)}
                                // style={sliderInit ? { height: 'auto' } : { height: '0' }}
                                style={
                                    {
                                        ...(sliderInit ? { height: 'auto' } : { height: '0' }),
                                        '--swiper-pagination-color': '#000',
                                        '--swiper-pagination-bullet-inactive-color': '#ccc',
                                        '--swiper-pagination-bullet-inactive-opacity': '1',
                                    } as React.CSSProperties
                                }
                            >
                                {ITEMS.map((item, i) => {
                                    return (
                                        <SwiperSlide
                                            key={i}
                                            style={sliderInit ? { opacity: '1' } : { opacity: '0' }}
                                            className="px-[10px] xl:px-[12px]"
                                        >
                                            <a
                                                href={`#`}
                                                target="_blank"
                                                className={cn(
                                                    'w-full rounded-[25px] overflow-hidden bg-white shadow-[0_3px_4px_rgba(0,0,0,0.08)]',
                                                    'transition-all duration-300 hover:-translate-y-[10px] hover:shadow-[0_9px_25px_0_rgba(0,0,0,0.15)]',
                                                    'hover:[&_.card-top]:bg-cust-primary hover:[&_.card-top-badge]:bg-[#f8e373]  hover:[&_.card-top-badge]:text-[#c5a218]',
                                                )}
                                            >
                                                <div className="flex flex-col items-center relative">
                                                    <div className="absolute top-[15px] left-[15px]">
                                                        <small className="bg-cust-primary text-[9px] font-[700] text-white py-[3px] px-[8px] rounded-[10px] flex items-center  transition-all duration-300 card-top-badge">
                                                            TOP
                                                        </small>
                                                    </div>
                                                    <div className="w-full h-[100px] bg-[#e6e6e6] transition-all duration-300 card-top"></div>
                                                    <div className="w-full flex justify-center">
                                                        <div
                                                            className={cn(
                                                                'flex justify-center items-center bg-white shadow-[0_8px_25px_rgba(0,0,0,0.12),_0_2px_6px_rgba(0,0,0,0.08)]',
                                                                'size-[120px] -translate-y-1/2',
                                                                props.type === 'broker'
                                                                    ? 'rounded-full p-[10px]'
                                                                    : 'rounded-[20px] p-[5px]',
                                                                'md:size-[100px]',
                                                                'lg:size-[120px]',
                                                                'xl:size-[130px]',
                                                            )}
                                                        >
                                                            <Img
                                                                src={
                                                                    item.photoUrl.length > 0
                                                                        ? item.photoUrl
                                                                        : '/images/image_placeholder.png'
                                                                }
                                                                className={cn(
                                                                    `w-full h-full object-cover`,
                                                                    props.type === 'broker'
                                                                        ? 'rounded-full'
                                                                        : 'rounded-[15px]',
                                                                )}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div
                                                        className={cn(
                                                            'flex gap-[5px] -mt-[40px]',
                                                            'md:-mt-[30px]',
                                                            'lg:-mt-[40px]',
                                                        )}
                                                    >
                                                        <StarFilledIcon className="fill-cust-primary size-[13px]" />
                                                        <StarFilledIcon className="fill-cust-primary size-[13px]" />
                                                        <StarFilledIcon className="fill-cust-primary size-[13px]" />
                                                        <StarFilledIcon className="fill-cust-primary size-[13px]" />
                                                        <StarFilledIcon className="fill-cust-primary size-[13px]" />
                                                    </div>
                                                </div>
                                                <div className="p-[20px] md:p-[15px] xl:p-[20px]">
                                                    <div className={cn('')}>
                                                        <div className="text-center">
                                                            <h4
                                                                className={cn(
                                                                    'text-[20px]',
                                                                    'md:text-[17px]',
                                                                    'lg:text-[20px]',
                                                                )}
                                                            >
                                                                {item.name}
                                                            </h4>
                                                            <p
                                                                className={cn(
                                                                    'text-[15px] font-[400] mt-[5px]',
                                                                    'md:text-[13px]',
                                                                    'lg:text-[15px]',
                                                                )}
                                                            >
                                                                {item.agencyName}
                                                            </p>
                                                            <p
                                                                className={cn(
                                                                    'text-[15px] font-[300]',
                                                                    'md:text-[13px]',
                                                                )}
                                                            >
                                                                {item.location}
                                                            </p>
                                                            {item.numberOfBrokers && (
                                                                <div className="flex gap-[3px] justify-center items-center">
                                                                    <p className="text-[12px] font-[700]">
                                                                        {item.numberOfBrokers}
                                                                    </p>
                                                                    <small className="text-[#777777] text-[12px]">
                                                                        maklérov
                                                                    </small>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="mt-[20px] flex justify-between items-center gap-[15px]">
                                                        <div
                                                            className={cn(
                                                                'flex gap-[5px] relative top-[5px] md:gap-[2px]',
                                                                'lg:gap-[5px]',
                                                            )}
                                                        >
                                                            {item.isMemberOfZrks && (
                                                                <div>
                                                                    <Img
                                                                        className={cn(
                                                                            'size-[40px]',
                                                                            'md:size-[30px]',
                                                                            'lg:size-[35px]',
                                                                        )}
                                                                        src={
                                                                            '/images/profile_logo_zrks.png'
                                                                        }
                                                                        alt={'zrks'}
                                                                    />
                                                                </div>
                                                            )}
                                                            {item.isMemberOfRealunia && (
                                                                <div>
                                                                    <Img
                                                                        className={cn(
                                                                            'size-[40px]',
                                                                            'md:size-[30px]',
                                                                            'lg:size-[35px]',
                                                                        )}
                                                                        src={
                                                                            '/images/profile_logo_real_unia.png'
                                                                        }
                                                                        alt={'realunia'}
                                                                    />
                                                                </div>
                                                            )}
                                                            {item.isMemberOfNarks && (
                                                                <div>
                                                                    <Img
                                                                        className={cn(
                                                                            'size-[40px]',
                                                                            'md:size-[30px]',
                                                                            'lg:size-[35px]',
                                                                        )}
                                                                        src={
                                                                            '/images/profile_logo_narks.png'
                                                                        }
                                                                        alt={'narks'}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex text-center">
                                                            <div>
                                                                <small
                                                                    className={cn(
                                                                        'font-[600] text-[#777777] text-[13px]',
                                                                        'md:text-[10px]',
                                                                        'lg:text-[13px]',
                                                                    )}
                                                                >
                                                                    Predaj:
                                                                </small>
                                                                <p
                                                                    className={cn(
                                                                        'font-[800] text-[20px] md:text-[17px]',
                                                                        'lg:text-[20px]',
                                                                    )}
                                                                >
                                                                    {item.saleCount}
                                                                </p>
                                                            </div>
                                                            <Separator
                                                                orientation="vertical"
                                                                className={cn(
                                                                    'mx-[15px]',
                                                                    'md:mx-[7px]',
                                                                )}
                                                            />
                                                            <div>
                                                                <small
                                                                    className={cn(
                                                                        'font-[600] text-[#777777] text-[13px]',
                                                                        'md:text-[10px]',
                                                                        'lg:text-[13px]',
                                                                    )}
                                                                >
                                                                    Prenájom:
                                                                </small>
                                                                <p
                                                                    className={cn(
                                                                        'font-[800] text-[20px] md:text-[17px]',
                                                                        'lg:text-[20px]',
                                                                    )}
                                                                >
                                                                    {item.rentCount}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </a>
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                        )}
                        <div
                            ref={paginationRef}
                            className="mt-4 flex items-center justify-center gap-2"
                        />
                    </>
                </div>
            </div>
        </div>
    );
}
